import { canvasRefAtom, mouseRefAtom, currentUserAtom } from "../jotai/atoms";
import { useUpdateAtom } from "jotai/utils";
import React, { useCallback, useEffect, useRef } from "react";
import { AnyBlock, Point2D } from "../types";
import Console from "../ui/Console";
import BlockList from "../blocks/BlockList";
import CursorList from "../av/CursorList";
import BlockPreview from "../blocks/BlockPreview";
import { keyframes, styled } from "../stitches.config";
import { handleDroppedImage, handlePaste } from "../utils/fileUpload";
import { DEFAULT_SPACE_SIZE, DEFAULT_GRID_RESOLUTION } from "../consts";
import Minimap from "./Minimap";
import { updateBlockMap, useBlockIds } from "../roomservice/maps";
import { useRoomClient } from "../roomservice/utils";
import CursorIndicators from "./CursorIndicators";
import { handleTextPaste } from "../blocks/LinkBlock";
import { useWindowSize } from "../utils/useWindowSize";
import useSpaceScroll from "../utils/useSpaceScroll";
import { gridToPixelSize } from "../utils/gridPosition";
import { useAtom } from "jotai";
import ViewportList from "../av/ViewportList";
import PreviewList from "../av/PreviewList";
import { MutedAudioWarning } from "./MutedAudioWarning";
import { useEdgeScroll } from "./EdgeScroll";
import { useStarterBlocks } from "./useStarterBlocks";
import { handleDroppedBlockData } from "./ExportJSON";
import Doorbell from "./Doorbell";

export const DEFAULT_SPACE_SIZE_PIXELS = gridToPixelSize(DEFAULT_SPACE_SIZE);

const DW = DEFAULT_SPACE_SIZE_PIXELS.width;
const DH = DEFAULT_SPACE_SIZE_PIXELS.height;

export default function Space() {
  const room = useRoomClient();
  const [blockIds, blockIdsClient] = useBlockIds();
  const setCanvasRefAtom = useUpdateAtom(canvasRefAtom);
  const windowSize = useWindowSize();

  const canvasRef = useRef<HTMLDivElement>(null);

  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const mousePositionRef = useRef<Point2D>({ x: 0, y: 0 });
  const setMouseRefAtom = useUpdateAtom(mouseRefAtom);
  useEffect(() => {
    setMouseRefAtom(mousePositionRef);
  }, [mousePositionRef, setMouseRefAtom]);

  useSpaceScroll();

  useEdgeScroll();

  const [currentUser] = useAtom(currentUserAtom);

  // Helpers to add/update blocks
  const upsertBlock = useCallback(
    (block: Partial<AnyBlock>, raise = true) => {
      if (!room || !blockIdsClient) return;
      if (!block.id) {
        throw new TypeError("Upsert called without block id");
      }
      const client = room.map<AnyBlock>(block.id);

      // if you create or an edit a block that doesn't have an "owner", you're the owner now
      if (!client.get("madeBy") && !block?.madeBy) {
        block.madeBy = currentUser?.id;
      }

      updateBlockMap(client, block, raise);
      blockIdsClient.set(block.id, block.id);
    },
    [room, blockIdsClient, currentUser]
  );

  const deleteBlock = useCallback(
    (blockId: string) => {
      if (blockIdsClient) blockIdsClient.delete(blockId);
    },
    [blockIdsClient]
  );

  // Pasting images
  useEffect(() => {
    if (!blockIdsClient) return;

    function onPaste(e: ClipboardEvent) {
      handlePaste(e.clipboardData, upsertBlock, mousePositionRef.current);

      if (
        document.activeElement?.nodeName !== "TEXTAREA" &&
        document.activeElement?.nodeName !== "INPUT"
      )
        handleTextPaste(e.clipboardData, upsertBlock, mousePositionRef.current);
    }

    function onDrop(e: DragEvent) {
      const items = e.dataTransfer?.items;
      if (items) {
        // handle dropped JSON block data
        handleDroppedBlockData(upsertBlock, e, items);
      }

      // handle dragged image from imageSearch
      try {
        let data = JSON.parse(e!.dataTransfer!.getData("text"));
        let { url, width, height } = data;
        if (url && width & height) {
          handleDroppedImage(width, height, mousePositionRef, url, upsertBlock);
        }
      } catch (e) {
        // unknown text drop
      }
      //handle dropped image
      handlePaste(e.dataTransfer, upsertBlock, mousePositionRef.current);
      e.preventDefault(); // prevent opening file when dragging file in
    }

    function onDragOver(e: DragEvent) {
      e.preventDefault(); // prevent opening file when dragging file in
    }

    document.addEventListener("paste", onPaste);
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragover", onDragOver);
    return () => {
      document.removeEventListener("paste", onPaste);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragover", onDragOver);
    };
  }, [blockIdsClient, upsertBlock]);

  // Styles
  const loadingStyle =
    blockIdsClient === undefined
      ? {
          animation: `${canvasLoadingAnimation} 2.5s infinite alternate`,
        }
      : {};

  // Get canvasRef to use instead of threading everywhere
  useEffect(() => {
    if (canvasRef?.current) {
      setCanvasRefAtom(canvasRef!.current!);
    }
  }, [canvasRef, setCanvasRefAtom]);

  let blockIdList = Object.keys(blockIds || {});
  useStarterBlocks(Object.keys(blockIdList), upsertBlock);

  return (
    <>
      <CanvasWrapper ref={canvasWrapperRef} id="#main">
        <CanvasBackground />

        <Canvas ref={canvasRef}>
          <CanvasGrid className="background" style={loadingStyle} />
          <ViewportList />
          <PreviewList />
          <BlockPreview upsertBlock={upsertBlock} />
          <BlockList
            blockIds={blockIdList}
            deleteBlock={deleteBlock}
            upsertBlock={upsertBlock}
          />
          <CursorList upsertBlock={upsertBlock} windowSize={windowSize} />
        </Canvas>
      </CanvasWrapper>
      <CursorIndicators />
      <Tools>
        <Console />
        <Doorbell />
        <MutedAudioWarning />
        <Minimap windowSize={windowSize} />
      </Tools>
    </>
  );
}
const Canvas = styled("main", {
  position: "relative",
  userSelect: "none",
  width: DW,
  height: DH,
});

const canvasLoadingAnimation = keyframes({
  "0%": { opacity: 0 },
});

const CanvasBackground = styled("div", {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  backgroundColor: "$beige",
});

const CanvasGrid = styled("div", {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  backgroundImage: `linear-gradient(to right, rgba(50,0,0, 0.1) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(50,0,0, 0.1) 1px, transparent 1px)`,
  backgroundSize: `${DEFAULT_GRID_RESOLUTION}px ${DEFAULT_GRID_RESOLUTION}px`,
  opacity: 0.95,
  pointerEvents: "none",
  transition: "opacity 2.5s linear", // handle removing of keyframe fade ?
});

const CanvasWrapper = styled("div", {
  position: "absolute",
  top: 0,
  left: 0,
  width: DW,
  height: DH,
  transformOrigin: "50% 50%",
  willChange: "transform",
});

const Tools = styled("div", {
  position: "fixed",
  bottom: "$2",
  right: "$2",
  display: "grid",
  gridAutoFlow: "column",
  gridGap: "$1",
  alignItems: "end",
});
